import React from "react";
import { Button, Checkbox, Form, Icon, Input, Select } from "antd";
import { debounce, uniq } from "lodash";
import { RopeLandingPageFilterSettings } from "../../types/RopeLandingPagePreferences";
import { RopeLandingPageData } from "../../types/Rope";

type RopeLandingPageFiltersProps = {
  data: RopeLandingPageData[];
  onSearch: (filteredProjects: RopeLandingPageData[]) => void;
  form: any;
  searchRef: any;
  disabled: boolean;
  settings: RopeLandingPageFilterSettings;
  setSettings: (settings: RopeLandingPageFilterSettings) => void;
};

export const filterRopeLandingPageData = (
  props: RopeLandingPageFiltersProps,
  changedValues: any,
  values: any
) => {
  const { data, onSearch, setSettings } = props;
  const uppercaseTerm = values.searchTerm
    ? values.searchTerm.toUpperCase()
    : "";
  const statuses = values.selectedStatuses;
  const clients = values.selectedClients;
  const offices = values.selectedOffices;
  const entities = values.selectedEntities;
  const projectCodes = values.selectedProjectCodes;
  const showInactive = values.showInactive;

  setSettings({
    search: values.searchTerm,
    status: statuses,
    client: clients,
    office: offices,
    entity: entities,
    projectCode: projectCodes,
    inactive: showInactive
  });

  const filteredProjects = data.filter(p => {
    let isSearchValid =
      !uppercaseTerm ||
      !uppercaseTerm.length ||
      (p.projectCode
        ? p.projectCode.toUpperCase().indexOf(uppercaseTerm) > -1
        : false) ||
      p.projectName.toUpperCase().indexOf(uppercaseTerm) > -1 ||
      p.clientName.toUpperCase().indexOf(uppercaseTerm) > -1 ||
      p.office.toUpperCase().indexOf(uppercaseTerm) > -1;
    let isStatusValid =
      !statuses || !statuses.length || statuses.indexOf(p.status) > -1;
    let isClientValid =
      !clients || !clients.length || clients.indexOf(p.clientName) > -1;
    let isOfficeValid =
      !offices || !offices.length || offices.indexOf(p.office) > -1;
    let isEntityValid =
      !entities || !entities.length || entities.indexOf(p.entity) > -1;
    let isProjectCodeValid =
      !projectCodes || !projectCodes.length || projectCodes.indexOf(p.projectCode) > -1;
    let isInactiveValid = showInactive || !p.inactive;
    return (
      isSearchValid &&
      isStatusValid &&
      isClientValid &&
      isOfficeValid &&
      isEntityValid &&
      isProjectCodeValid &&
      isInactiveValid
    );
  });
  onSearch(filteredProjects);
};

const RopeLandingPageFilters = (props: RopeLandingPageFiltersProps) => {
  const { data, form, searchRef, disabled, settings } = props;
  const { getFieldDecorator } = form;
  const statuses = uniq<string>(data.map(p => p.status)).sort();
  const selectedStatuses = form.getFieldValue("selectedStatuses") || [];
  const clients = uniq<string>(data.map(p => p.clientName)).sort();
  const selectedClients = form.getFieldValue("selectedClients") || [];
  const offices = uniq<string>(data.map(p => p.office)).sort();
  const selectedOffices = form.getFieldValue("selectedOffices") || [];
  const entities = uniq<string>(data.map(p => p.entity)).sort();
  const selectedEntities = form.getFieldValue("selectedEntities") || [];
  const projectCodes = uniq<string>(data.map(p => p.projectCode)).sort();
  const selectedProjectCodes = form.getFieldValue("selectedProjectCodes") || [];

  const clearFilters = () => {
    form.resetFields();
    filterRopeLandingPageData(
      props,
      {},
      {
        searchTerm: "",
        selectedStatuses: [],
        selectedClients: [],
        selectedOffices: [],
        selectedEntities: [],
        selectedProjectCodes: [],
        showInactive: false
      }
    );
  };

  return (
    <div style={disabled ? { pointerEvents: "none", opacity: 0.4 } : {}}>
      <Form layout="inline">
        <Form.Item>
          {getFieldDecorator("searchTerm", {
            initialValue: settings.search
          })(
            <Input
              prefix={<Icon type="filter" />}
              ref={searchRef}
              disabled={disabled}
              placeholder="Filter"
            />
          )}
        </Form.Item>

        <Form.Item>
          {getFieldDecorator("selectedStatuses", {
            initialValue: settings.status
          })(
            <Select
              maxTagCount={0}
              maxTagPlaceholder={`${selectedStatuses.length} Status${
                selectedStatuses.length === 1 ? "" : "es"
              } Selected`}
              showArrow
              mode="multiple"
              placeholder="Status"
              dropdownClassName="filter-bar-select-dropdown"
              className="filter-bar-select"
            >
              {statuses.map(m => (
                <Select.Option key={m} value={m}>
                  {m}
                </Select.Option>
              ))}
            </Select>
          )}
        </Form.Item>

        <Form.Item>
          {getFieldDecorator("selectedClients", {
            initialValue: settings.client
          })(
            <Select
              maxTagCount={0}
              maxTagPlaceholder={`${selectedClients.length} Client${
                selectedClients.length === 1 ? "" : "s"
              } Selected`}
              showArrow
              mode="multiple"
              placeholder="Client"
              dropdownClassName="filter-bar-select-dropdown"
              className="filter-bar-select"
            >
              {clients.map(m => (
                <Select.Option key={m} value={m}>
                  {m}
                </Select.Option>
              ))}
            </Select>
          )}
        </Form.Item>

        <Form.Item>
          {getFieldDecorator("selectedOffices", {
            initialValue: settings.office
          })(
            <Select
              maxTagCount={0}
              maxTagPlaceholder={`${selectedOffices.length} Office${
                selectedOffices.length === 1 ? "" : "s"
              } Selected`}
              showArrow
              mode="multiple"
              placeholder="Office"
              dropdownClassName="filter-bar-select-dropdown"
              className="filter-bar-select"
            >
              {offices.map(m => (
                <Select.Option key={m} value={m}>
                  {m}
                </Select.Option>
              ))}
            </Select>
          )}
        </Form.Item>

        <Form.Item>
          {getFieldDecorator("selectedEntities", {
            initialValue: settings.entity
          })(
            <Select
              maxTagCount={0}
              maxTagPlaceholder={`${selectedEntities.length} Entit${
                selectedEntities.length === 1 ? "y" : "ies"
              } Selected`}
              showArrow
              mode="multiple"
              placeholder="Entity"
              dropdownClassName="filter-bar-select-dropdown"
              className="filter-bar-select"
            >
              {entities.map(m => (
                <Select.Option key={m} value={m}>
                  {m}
                </Select.Option>
              ))}
            </Select>
          )}
        </Form.Item>

        <Form.Item>
          {getFieldDecorator("selectedProjectCodes", {
            initialValue: settings.projectCode
          })(
            <Select
              maxTagCount={0}
              maxTagPlaceholder={`${selectedProjectCodes.length} Project Code${
                selectedProjectCodes.length === 1 ? "" : "s"
              } Selected`}
              showArrow
              mode="multiple"
              placeholder="Project Code"
              dropdownClassName="filter-bar-select-dropdown"
              className="filter-bar-select"
            >
              {projectCodes.map(m => (
                <Select.Option key={m} value={m}>
                  {m}
                </Select.Option>
              ))}
            </Select>
          )}
        </Form.Item>

        <Form.Item>
          {getFieldDecorator("showInactive", {
            initialValue: settings.inactive,
            valuePropName: "checked"
          })(<Checkbox>Show Inactive</Checkbox>)}
        </Form.Item>

        <Form.Item>
          <Button
            title="Clear filters"
            icon="close"
            type="link"
            onClick={clearFilters}
          >
            Clear filters
          </Button>
        </Form.Item>
      </Form>
    </div>
  );
};

export default Form.create<RopeLandingPageFiltersProps>({
  name: "filter_rope_landing_page_form",
  onValuesChange: debounce(filterRopeLandingPageData, 500)
})(RopeLandingPageFilters);
